<div class="board">
  <div class="head">
    <div>
      <h2>{{ title }}</h2>
      <p class="sub">{{ subtitle }}</p>
    </div>
    <div class="head-actions">
      <app-range-switch [days]="days" (daysChange)="setRange($event)"></app-range-switch>
      <button mat-stroked-button (click)="load()" [disabled]="loading">Refresh</button>
    </div>
  </div>

  <div class="notice ok" *ngIf="notice">{{ notice }}</div>
  <div class="notice bad" *ngIf="error">{{ error }}</div>

  <ng-container *ngIf="overview as o">
    <!-- The funnel is the point of the page: each step lost is a different fix. -->
    <div class="funnel">
      <div class="step">
        <div class="n">{{ o.totals.conversations }}</div>
        <div class="l">Conversations started</div>
      </div>
      <div class="arrow">→</div>
      <div class="step" [class.bad]="o.totals.unopened > 0">
        <div class="n">{{ o.totals.unopened }}</div>
        <div class="l">Never opened<app-info-hint label="Never opened"
          text="Nobody but the sender has a read receipt at or after the first message. Only as trustworthy as read-receipt coverage — the warning below this row fires when too few chats carry one."
        ></app-info-hint></div>
        <div class="hint">notification problem</div>
      </div>
      <div class="arrow">→</div>
      <div class="step" [class.warn]="o.totals.openedNoReply > 0">
        <div class="n">{{ o.totals.openedNoReply }}</div>
        <div class="l">Opened, no reply<app-info-hint label="Opened, no reply"
          text="Someone other than the sender opened it, but only one person ever sent a message."
        ></app-info-hint></div>
        <div class="hint">relevance problem</div>
      </div>
      <div class="arrow">→</div>
      <div class="step good">
        <div class="n">{{ o.totals.replied }}</div>
        <div class="l">Got a reply<app-info-hint label="Got a reply"
          text="More than one person sent a message. The ones that went on to change speaker at least 4 times are counted separately as conversations."
        ></app-info-hint></div>
        <div class="hint">{{ o.totals.conversations2Way }} became conversations</div>
      </div>
    </div>

    <div class="stats">
      <div class="stat">
        <div class="n">{{ replyRate !== null ? replyRate + '%' : '—' }}</div>
        <div class="l">Reply rate<app-info-hint label="Reply rate"
          text="Share of started chats where a second person sent something back."
        ></app-info-hint></div>
      </div>
      <div class="stat">
        <div class="n">{{ o.medianTurns ?? '—' }}</div>
        <div class="l">Median turns<app-info-hint label="Median turns"
          text="The middle chat's turn count: half the chats in range sit above it, half below. Median rather than average, so one long thread cannot flatter the whole board."
        ></app-info-hint></div>
      </div>
      <div class="stat">
        <div class="n">{{ o.totals.distinctInitiators }}</div>
        <div class="l">People starting chats</div>
      </div>
      <div class="stat" *ngIf="!fixedKind">
        <div class="n">{{ o.peer.conversations }} / {{ o.lobby.conversations }}</div>
        <div class="l">Peer / lobby<app-info-hint label="Peer / lobby"
          text="Chats started one-to-one between two people, against those started in an event's lobby room."
        ></app-info-hint></div>
      </div>
      <div class="stat">
        <div class="n">{{ o.totals.reactions }}</div>
        <div class="l">Reactions</div>
      </div>
    </div>

    <!-- The funnel is only as good as the field it rests on, so the page says
         how much of that field it actually has. -->
    <div class="notice warn" *ngIf="o.readReceiptCoverage < 0.5">
      Read receipts are present on only {{ (o.readReceiptCoverage * 100) | number: '1.0-0' }}% of
      these chats. "Never opened" counts are unreliable below that — treat the first
      funnel step as unknown rather than as a notification failure.
    </div>

    <app-daily-bars *ngIf="o.daily.length" title="Conversations started per day" [bars]="dailyBars">
      {{ o.totals.messages }} messages in range
    </app-daily-bars>
  </ng-container>

  <div class="filters">
    <button [class.on]="filter === 'all'" (click)="filter = 'all'">All</button>
    <button [class.on]="filter === 'unopened'" (click)="filter = 'unopened'">Never opened</button>
    <button [class.on]="filter === 'opened-no-reply'" (click)="filter = 'opened-no-reply'">
      Opened, no reply
    </button>
    <button [class.on]="filter === 'replied'" (click)="filter = 'replied'">Replied</button>
    <button [class.on]="filter === 'conversation'" (click)="filter = 'conversation'">
      Conversations
    </button>
    <ng-container *ngIf="!fixedKind">
      <span class="spacer"></span>
      <button [class.on]="kind === 'all'" (click)="kind = 'all'">Both</button>
      <button [class.on]="kind === 'peer'" (click)="kind = 'peer'">Peer</button>
      <button [class.on]="kind === 'lobby'" (click)="kind = 'lobby'">Lobby</button>
    </ng-container>
  </div>

  <div class="loading" *ngIf="loading"><mat-spinner diameter="28"></mat-spinner></div>

  <table *ngIf="!loading && rows.length">
    <thead>
      <tr>
        <th>Who</th><th *ngIf="!fixedKind">Kind</th>
        <th>State<app-info-hint label="State"
          text="How far it got: never opened, opened but unanswered, replied, or a conversation once the speaker has changed 4 times."
        ></app-info-hint></th>
        <th class="num">Messages<app-info-hint label="Messages"
          text="Messages sent in this chat, everyone's together. Reactions are counted apart from these."
        ></app-info-hint></th>
        <th class="num">Turns<app-info-hint label="Turns"
          text="How many times the speaker changed hands. A monologue scores 0 however many messages it holds; 4 is where a reply is treated as a conversation."
        ></app-info-hint></th>
        <th class="num">Voices<app-info-hint label="Voices"
          text="How many different people sent at least one message. 1 means nobody ever answered."
        ></app-info-hint></th>
        <th>Started</th><th>Last message</th>
      </tr>
    </thead>
    <tbody>
      <tr *ngFor="let c of rows" [attr.data-state]="c.state">
        <td>
          <div class="title">
            <ng-container *ngFor="let p of c.people; let i = index">
              <span class="sep" *ngIf="i"> · </span>
              <app-person-link
                [uid]="p.uid"
                [name]="p.name"
                (failed)="error = $event"
              ></app-person-link>
            </ng-container>
            <span *ngIf="!c.people.length">—</span>
          </div>
          <!-- A lobby chat belongs to a space, and its id is that space's id:
               the row is the way into the space, not just a label for it. -->
          <div class="meta" *ngIf="c.kind === 'lobby'; else peerId">
            <app-event-link [eventId]="c.id" [title]="c.eventTitle"></app-event-link>
          </div>
          <ng-template #peerId><div class="meta">{{ c.id }}</div></ng-template>
        </td>
        <td class="meta" *ngIf="!fixedKind">{{ c.kind }}</td>
        <td><span class="pill" [attr.data-state]="c.state">{{ stateLabel(c.state) }}</span></td>
        <td class="num">{{ c.messageCount }}</td>
        <td class="num">{{ c.turns }}</td>
        <td class="num">{{ c.distinctSenders }}</td>
        <td class="meta">{{ c.firstMessageAt ? (c.firstMessageAt | date: 'd MMM, HH:mm') : '—' }}</td>
        <td class="meta">{{ c.lastMessageAt ? (c.lastMessageAt | date: 'd MMM, HH:mm') : '—' }}</td>
      </tr>
    </tbody>
  </table>

  <p class="empty" *ngIf="!loading && !rows.length">
    No conversations match. If the whole board is empty, the summaries have not been
    built yet — conversations from before this panel existed need one backfill pass.
  </p>

  <div class="backfill">
    <button mat-stroked-button (click)="runBackfill()" [disabled]="backfilling">
      {{ backfilling ? 'Backfilling…' : 'Backfill history' }}
    </button>
    <span class="meta" *ngIf="backfillProgress && backfilling">
      {{ backfillProgress.conversations }} conversations, {{ backfillProgress.messages }} messages…
    </span>
    <span class="meta" *ngIf="!backfilling">
      Reads every existing chat once and builds its summary. Safe to re-run — it skips
      conversations already summarised.
    </span>
  </div>
</div>
